import { Grid, Popover, Typography } from '@mui/material';
import { DetailsRow } from 'lib/ClientDataDetails/DetailsRow';
import { generalEnum } from 'screens/Clients/utils/generalEnum';
import TemplateDetails from '../TemplateDetails';
import { useTranslation } from 'react-i18next';
import { LoanPartRowProps } from 'types/Client';
import { getStartdate } from 'screens/Clients/utils/calculateLoanParts';
import { formatLoanNumber } from 'utils/global';
import { ClientExtractLoanPartCollectionIntervalEnum } from 'types/ClientDetails/Enums/ClientExtractLoanPartCollectionIntervalEnum';
import { ClientExtractLoanPartGuaranteeEnum } from 'types/ClientDetails/Enums/ClientExtractLoanPartGuaranteeEnum';
import { useRef, useState } from 'react';

import { ClientExtractLoanPartRedemptionMethodEnum } from 'types/ClientDetails/Enums/ClientExtractLoanPartRedemptionMethodEnum';
import { PopoverWrapper } from './style/LoanPartsDetails.style';

export default function LoanPartDetails({ translationsRoot, loanPart }: LoanPartRowProps) {
  const { t } = useTranslation();
  const [isInfoBoxOpen, setisInfoBoxOpen] = useState<boolean>(false);
  const startDate = getStartdate(loanPart.interestPeriod);
  const loanNumber = loanPart.number ? formatLoanNumber(loanPart.number) : '-';
  const installmentTotalAmountInput = useRef<HTMLDivElement | null>(null);

  return (
    <Grid container>
      <Grid item xs={12} md={6} p={2}>
        <TemplateDetails title={t(`${translationsRoot}.headerLoanpart`)}>
          <DetailsRow
            type="string"
            label={t(`${translationsRoot}.number`)}
            value={loanNumber}
            isLoanPart
          />
          <DetailsRow
            type="currency"
            label={t(`${translationsRoot}.principalSum`)}
            value={loanPart.principalSum}
            isLoanPart
          />
          <DetailsRow
            type="currency"
            label={t(`${translationsRoot}.residualDebt`)}
            value={loanPart.residualDebt}
            isLoanPart
          />
          <DetailsRow
            type="date"
            label={t(`${translationsRoot}.startDateLoanpart`)}
            value={loanPart.startDate}
            isLoanPart
          />
          <DetailsRow
            type="date"
            label={t(`${translationsRoot}.endDateLoanpart`)}
            value={loanPart.endDate}
            isLoanPart
          />
          <DetailsRow
            label={t(`${translationsRoot}.duration`)}
            value={loanPart.duration}
            isLoanPart
          />
          <DetailsRow
            type="string"
            label={t(`${translationsRoot}.technicalProductName`)}
            value={loanPart.product}
            isLoanPart
          />
          <DetailsRow
            type="string"
            label={t(`${translationsRoot}.redemptionMethod`)}
            value={generalEnum(
              'redemptionMethod',
              loanPart.redemptionMethod,
              ClientExtractLoanPartRedemptionMethodEnum
            )}
            isLoanPart
          />
          <DetailsRow
            type="string"
            label={t(`${translationsRoot}.collectionInterval`)}
            value={generalEnum(
              'collectionInterval',
              loanPart.collectionInterval,
              ClientExtractLoanPartCollectionIntervalEnum
            )}
            isLoanPart
          />
          <DetailsRow
            type="string"
            label={t(`${translationsRoot}.warrantytype`)}
            value={generalEnum('guarantee', loanPart.guarantee, ClientExtractLoanPartGuaranteeEnum)}
            isLoanPart
          />
        </TemplateDetails>
      </Grid>
      <Grid item xs={12} md={6} p={2}>
        <TemplateDetails title={t(`${translationsRoot}.headerInterest`)}>
          <DetailsRow
            type="date"
            label={t(`${translationsRoot}.startDateInterestPeriod`)}
            value={startDate}
            isLoanPart
          />
          <DetailsRow
            type="date"
            label={t(`${translationsRoot}.endDateInterestPeriod`)}
            value={loanPart.interestPeriod?.renewalDate}
            isLoanPart
          />
          <DetailsRow
            label={t(`${translationsRoot}.fixedInterestPeriod`)}
            value={loanPart.interestPeriod?.duration}
            isLoanPart
          />
          <DetailsRow
            type="percentage"
            label={t(`${translationsRoot}.interestRate`)}
            value={loanPart.interestPeriod?.effectiveInterestRate}
            isLoanPart
          />
          <DetailsRow
            label={t(`${translationsRoot}.remainingPeriod`)}
            value={loanPart.interestPeriod?.remainingDuration}
            isLoanPart
          />
        </TemplateDetails>
        <TemplateDetails title={t(`${translationsRoot}.installment`)}>
          <DetailsRow
            type="date"
            label={t(`${translationsRoot}.installmentDate`)}
            value={loanPart.collectionOrderDate}
            isLoanPart
          />
          <div
            ref={installmentTotalAmountInput}
            onMouseEnter={() => setisInfoBoxOpen(true)}
            onMouseLeave={() => setisInfoBoxOpen(false)}
            onClick={() => setisInfoBoxOpen(!isInfoBoxOpen)}
          >
            <DetailsRow
              type="currency"
              isInfoBoxOpen
              label={t(`${translationsRoot}.installmentTotalAmount`)}
              value={loanPart.installment?.totalAmount}
              isLoanPart
            />
            <Popover
              data-testid="partOfLoan-popover"
              anchorEl={installmentTotalAmountInput.current}
              open={isInfoBoxOpen}
              onClick={() => setisInfoBoxOpen(false)}
              slotProps={{
                paper: {
                  style: {
                    maxWidth: '386px',
                    borderRadius: '0',
                  },
                },
              }}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 17,
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              disableRestoreFocus
            >
              <PopoverWrapper>
                <Typography variant="body1" color="primary">
                  {t(`${translationsRoot}.installmentTotalAmountInfoMessage`)}
                </Typography>
              </PopoverWrapper>
            </Popover>
          </div>
          <DetailsRow
            type="currency"
            label={t(`${translationsRoot}.installmentInterestAmount`)}
            value={loanPart.installment?.interestAmount}
            isLoanPart
          />
          <DetailsRow
            type="currency"
            label={t(`${translationsRoot}.installmentRedemptionAmount`)}
            value={loanPart.installment?.redemptionAmount}
            isLoanPart
          />
        </TemplateDetails>
      </Grid>
    </Grid>
  );
}
